import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { useTranslate } from "src/locales";
import { isPatientUnder18, isPatientNewborn } from "../../../utils";
import { getPainIcon } from "src/utils/painIconHelper";
import buttonList, { GridItem } from "./buttonList";
import { OpenedMedcards } from "src/modules/medcard/opened-medcars";
import TreatmentApplicationsModal from "src/modules/nurse/order-medicine";

const DetailPanel = ({ data }: any) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const { t: tCommon } = useTranslate();
  const [openedCardsModalOpen, setOpenedCardsModalOpen] = useState(false);
  const [treatmentApplicationsModalOpen, setTreatmentApplicationsModalOpen] = useState(false);

  // Backend'den gelecek field'lara göre ikonların aktif/pasif durumunu belirle
  const getIconActive = (iconName: string) => {
    // backend alanları lower-case geliyor
    const hasAllergy = data?.hasallergy ?? data?.hasAllergy;
    const hasAirWay = data?.hasairway ?? data?.hasAirWay;
    const hasBloodRequest = data?.hasbloodrequest ?? data?.hasBloodRequest;
    const hasConsultation = data?.hasconsultation ?? data?.hasConsultation;
    const hasEpikriz = data?.hasepikriz ?? data?.hasEpikriz;
    const hasPain = data?.haspain ?? data?.hasPain;
    const hasFallRisk = data?.hasfallrisk ?? data?.hasFallRisk;
    const hasFallen = data?.hasfallen ?? data?.hasFallen;
    const hasCompanion = data?.hascompanion ?? data?.hasCompanion;
    const hasProsthesis = data?.hasprosthesis ?? data?.hasProsthesis;
    const isPregnant = data?.ispregnant ?? data?.isPregnant;
    const hasRation = data?.hasration ?? data?.hasRation;

    const checkActive = (val: any) => val === 1 || val === true || val === "1";

    switch (iconName) {
      case "nurse.patient-list.detail-grid.allergy":
        return checkActive(hasAllergy);
      case "nurse.patient-list.detail-grid.pain":
        return checkActive(hasPain);
      case "nurse.patient-list.detail-grid.fall-risk":
        return checkActive(hasFallRisk);
      case "nurse.patient-list.detail-grid.falled":
        return checkActive(hasFallen);
      case "nurse.patient-list.detail-grid.air-way":
        return checkActive(hasAirWay);
      case "nurse.patient-list.detail-grid.protez":
        return checkActive(hasProsthesis);
      case "nurse.patient-list.detail-grid.pregnant":
        return checkActive(isPregnant);
      case "nurse.patient-list.detail-grid.companion":
        return checkActive(hasCompanion);
      case "nurse.patient-list.detail-grid.consultation":
        return checkActive(hasConsultation);
      case "nurse.patient-list.detail-grid.epicrisis":
        return checkActive(hasEpikriz);
      case "nurse.patient-list.detail-grid.blood-group":
        return checkActive(hasBloodRequest);
      case "nurse.patient-list.detail-grid.diet":
      case "nurse.patient-list.detail-grid.pediatri":
        return checkActive(hasRation);
      default:
        return false;
    }
  };

  const handleModalOpen = (modalType: string) => {
    switch (modalType) {
      case "opened-medcards":
        setOpenedCardsModalOpen(true);
        break;
      case "treatment-applications":
        setTreatmentApplicationsModalOpen(true);
        break;
      // Future modal types can be added here
      default:
        console.warn(`Unknown modal type: ${modalType}`);
    }
  };

  const handleModalClose = () => {
    setOpenedCardsModalOpen(false);
    setTreatmentApplicationsModalOpen(false);
  };

  // Process the buttonList to add active states and values
  const gridItems: GridItem[] = buttonList
    .map((item) => {
      // Handle button and image items
      const processedItem: GridItem = { ...item };

      // Only process image items that need active state and values
      if (processedItem.name) {
        processedItem.active = getIconActive(processedItem.name);

        if (processedItem.name === "nurse.patient-list.detail-grid.blood-group") {
          processedItem.value = data?.bloodgroup || data?.bloodGroup || null;
        }

        if (processedItem.name === "nurse.patient-list.detail-grid.pain") {
          const hasPain = data?.haspain ?? data?.hasPain;
          // Use the helper to determine the correct icon path
          // It will return processedItem.image (default) if hasPain is null/0
          processedItem.image = getPainIcon(hasPain, processedItem.image);
        }
      }
      return processedItem;
    });

  /** Inline styles to replace Chat.module.css */
  const styles = {
    chat: {
      width: "100%",
      position: "relative" as const,
      borderRadius: "12px",
      backgroundColor: "rgba(41, 121, 195, 0.1)",
      border: "2px solid rgba(28, 108, 229, 0.2)",
      boxSizing: "border-box" as const,
      overflow: "visible",
      display: "flex",
      flexDirection: "column" as const,
      alignItems: "stretch", // Stretch full width
      padding: "24px",
      gap: "20px", // More gap between rows
      textAlign: "left" as const,
      fontSize: "11px",
      color: "#000",
      fontFamily: "Poppins, sans-serif",
    },
    baseParent: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start", // Left align
      gap: isMobile ? "12px" : "40px", // 40px gap as requested
      flexWrap: "wrap" as const,
      width: "100%",
      flexDirection: isMobile ? "column" as const : "row" as const,
    },
    base: {
      borderRadius: "20px",
      backgroundColor: "#fff",
      display: "flex",
      alignItems: "center",
      padding: "8px 16px",
      minHeight: "44px",
      boxSizing: "border-box" as const,
      flex: "none",
      width: isMobile ? "100%" : "191px",
      maxWidth: "100%",
    },
    // base2 is essentially the same structure now
    base2: {
      borderRadius: "20px",
      backgroundColor: "#fff",
      display: "flex",
      alignItems: "center",
      padding: "8px 16px",
      minHeight: "44px",
      boxSizing: "border-box" as const,
      flex: "none",
      width: isMobile ? "100%" : "191px",
      maxWidth: "100%",
    },
    labIconParent: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      width: "100%",
    },
    labIcon: {
      height: "24px",
      width: "24px",
      position: "relative" as const,
      objectFit: "contain" as const,
    },
    button: {
      position: "relative" as const,
      fontSize: "11px",
      lineHeight: "26px",
      textTransform: "capitalize" as const,
      fontFamily: "Poppins",
      color: "#000",
      textAlign: "left" as const,
    },
    link: {
      textDecoration: "none",
      color: "inherit",
      display: "flex",
      width: "100%",
    }
  };

  const renderItem = (item: GridItem, itemIndex: number) => {
    // Gap check removed here implicitly as gridItems won't contain gaps

    const baseStyle = styles.base; // Simplified since all items have same base style now
    const contentStyle = styles.labIconParent;

    if (item.type === 'button') {
      const isNewbornButton = item.name === "nurse.patient-list.detail-grid.newborn-trace";
      const isPediatricButton = item.name === "nurse.patient-list.detail-grid.pediatri";
      const isNewbornAllowed = !isNewbornButton || isPatientNewborn(data?.age);
      const isPediatricAllowed = !isPediatricButton || isPatientUnder18(data?.age);

      // Always show the button, but disable it if not allowed
      let isButtonDisabled = item.disabled === true;
      let tooltipText = undefined;

      if (isNewbornButton && !isNewbornAllowed) {
        isButtonDisabled = true;
        tooltipText = "Sadece yeni doğan hastalar için"; // Consider using t() for localization
      } else if (isPediatricButton && !isPediatricAllowed) {
        isButtonDisabled = true;
        tooltipText = "Sadece 18 yaş altı hastalar için"; // Consider using t() for localization
      } else if (isButtonDisabled) {
        // Fallback for normally disabled buttons (under development)
        tooltipText = item.tooltip
          ? (typeof item.tooltip === "string" ? tCommon(item.tooltip) : item.tooltip)
          : tCommon("under-development");
      } else if (item.tooltip) {
        // Tooltip for active buttons if they have one
        tooltipText = typeof item.tooltip === "string" ? tCommon(item.tooltip) : item.tooltip;
      }


      const handleClick = (e: React.MouseEvent) => {
        if (item.modal) {
          e.preventDefault();
          handleModalOpen(item.modal);
        }
      };

      const imageElement = (
        <img
          src={item.image}
          alt={t(item.name || "")}
          style={{
            ...styles.labIcon,
            filter: isButtonDisabled ? "grayscale(100%)" : "none",
            opacity: isButtonDisabled ? 0.5 : 1
          }}
        />
      );

      const labelElement = (
        <div style={{ ...styles.button, opacity: isButtonDisabled ? 0.5 : 1 }}>
          {t(item.name || "")}
        </div>
      );

      let content;
      if (isButtonDisabled) {
        content = (
          <div style={{ ...baseStyle, cursor: "not-allowed" }}>
            <div style={contentStyle}>
              {imageElement}
              {labelElement}
            </div>
          </div>
        );
      } else {
        content = (
          <Link
            to={item.url ? item.url + data.id : "#"}
            onClick={handleClick}
            style={{ ...baseStyle, ...styles.link }}
          >
            <div style={contentStyle}>
              {imageElement}
              {labelElement}
            </div>
          </Link>
        );
      }

      if (tooltipText) {
        return (
          <Tooltip key={itemIndex} title={tooltipText} arrow>
            <Box sx={{ width: isMobile ? "100%" : "191px", maxWidth: "100%" }}> {/* Ensure tooltip wraps properly */}
              {content}
            </Box>
          </Tooltip>
        );
      }
      // Wrapping in a div to ensure flex sizing works if not tooltipped
      return <div key={itemIndex} style={{ width: isMobile ? "100%" : "191px", maxWidth: "100%" }}>{content}</div>;

    } else {
      // Image type logic
      const isActive = Boolean(item.active);

      return (
        <div key={itemIndex} style={{ ...baseStyle, width: isMobile ? "100%" : "191px", maxWidth: "100%" }}>
          <div style={contentStyle}>
            <img
              src={item.image}
              alt={t(item.name || "")}
              style={{
                ...styles.labIcon,
                filter: isActive ? "none" : "grayscale(100%)"
              }}
            />
            <div style={styles.button}>
              {item.value || t(item.name || "")}
            </div>
          </div>
        </div>
      );
    }
  };

  return (
    <Box sx={{ padding: { xs: "8px 0", lg: "16px 0" }, backgroundColor: "#fff" }}>
      <div style={styles.chat}>
        <div style={styles.baseParent}>
          {gridItems.map((item, itemIndex) => renderItem(item, itemIndex))}
        </div>
      </div>

      <OpenedMedcards
        open={openedCardsModalOpen}
        onClose={handleModalClose}
        ptId={data?.id}
        upn={data?.upn}
      />

      <TreatmentApplicationsModal
        open={treatmentApplicationsModalOpen}
        onClose={handleModalClose}
        patientId={data?.id}
        upn={data?.upn}
        patientName={data?.patientfullname || data?.patientFullName || data?.name}
        gender={data?.gender || data?.sex}
        isPregnant={data?.ispregnant || data?.isPregnant}
      />
    </Box>
  );
};

export default DetailPanel;
